// Calendar Module
// Handles FullCalendar initialization and events

import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Modal } from 'bootstrap';

export class CalendarManager {
    constructor() {
        this.calendars = new Map();
        this.setupResizeHandler();
    }
    
    // Setup resize handler for responsive calendar
    setupResizeHandler() {
        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                this.handleResize();
            }, 250);
        });
    }
    
    // Handle window resize
    handleResize() {
        const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        
        this.calendars.forEach((calendar, key) => {
            if (calendar) {
                // Update header toolbar based on screen size
                const newHeaderToolbar = isMobile ? {
                    left: 'prev,next',
                    center: 'title',
                    right: 'today'
                } : {
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay'
                };
                
                calendar.setOption('headerToolbar', newHeaderToolbar);
                calendar.render();
            }
        });
    }

    // Auto-initialize calendar based on page
    initCalendar() {
        
        // Check for member calendar (both possible IDs)
        const memberCalendarEl = document.getElementById('appointment-calendar') || document.getElementById('member-calendar');
        if (memberCalendarEl) {
            this.initializeMemberCalendar(memberCalendarEl);
            return;
        }
        
        // Check for appraiser calendar
        const appraiserCalendarEl = document.getElementById('appraiser-calendar');
        if (appraiserCalendarEl) {
            this.initializeAppraiserCalendar(appraiserCalendarEl);
            return;
        }
        
    }

    // Initialize member appointment calendar
    initializeMemberCalendar(element, options = {}) {
        // Check if mobile device - more comprehensive detection
        const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        
        const defaultOptions = {
            initialView: 'dayGridMonth',
            headerToolbar: isMobile ? {
                left: 'prev,next',
                center: 'title',
                right: 'today'
            } : {
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay'
            },
            slotLabelFormat: {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            },
            eventTimeFormat: {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            },
            events: (info, successCallback, failureCallback) => {
                this.loadAvailabilityEvents(info.start, info.end, successCallback, failureCallback);
            },
            eventClick: (info) => {
                if (info.event.extendedProps.available) {
                    this.selectTimeSlot(info.event);
                } else {
                    // Check if this is a member calendar and the slot is unavailable (booked by others)
                    const currentUserRole = this.getCurrentUserRole();
                    if (currentUserRole === 'member' && info.event.title.includes('Unavailable')) {
                        // Don't show popup for unavailable slots in member calendar (privacy protection)
                        return;
                    }
                    this.showBookedSlotInfo(info.event);
                }
            },
            eventDidMount: (info) => {
                if (info.event.extendedProps.available) {
                    info.el.style.cursor = 'pointer';
                    info.el.classList.add('fc-event-available');
                } else {
                    // Check if this is an unavailable slot in member calendar
                    const currentUserRole = this.getCurrentUserRole();
                    if (currentUserRole === 'member' && info.event.title.includes('Unavailable')) {
                        info.el.style.cursor = 'default';
                        info.el.classList.add('fc-event-unavailable');
                    } else {
                        info.el.style.cursor = 'not-allowed';
                        info.el.classList.add('fc-event-booked');
                    }
                }
            }
        };

        const calendar = new Calendar(element, { 
            plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
            ...defaultOptions, 
            ...options 
        });
        calendar.render();
        
        this.calendars.set('member', calendar);
        
        // Set up appraiser selection handler
        this.setupAppraiserSelection();
        
        return calendar;
    }

    // Initialize appraiser calendar
    initializeAppraiserCalendar(element, options = {}) {
        
        // Check if mobile device - more comprehensive detection
        const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        
        const defaultOptions = {
            initialView: 'dayGridMonth',
            headerToolbar: isMobile ? {
                left: 'prev,next',
                center: 'title',
                right: 'today'
            } : {
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay'
            },
            slotLabelFormat: {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            },
            eventTimeFormat: {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            },
            events: (info, successCallback, failureCallback) => {
                this.loadAppraiserEvents(info.start, info.end, successCallback, failureCallback);
            },
            eventClick: (info) => {
                this.handleAppraiserEventClick(info);
            },
            dateClick: (info) => {
            }
        };

        const calendar = new Calendar(element, { 
            plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
            ...defaultOptions, 
            ...options 
        });
        calendar.render();
        
        this.calendars.set('appraiser', calendar);
        return calendar;
    }

    // Load availability events for member calendar
    loadAvailabilityEvents(start, end, successCallback, failureCallback) {
        // Check if AJAX object is available
        if (typeof window.appraisal_ajax === 'undefined') {
            console.error('Calendar module: appraisal_ajax object not available');
            failureCallback('AJAX configuration not available');
            return;
        }
        
        // Show all availability from all appraisers
        const params = {
            action: 'get_availability_events',
            start: start.toISOString(),
            end: end.toISOString(),
            nonce: window.appraisal_ajax.nonce
        };

        fetch(window.appraisal_ajax.ajax_url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams(params)
        })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                successCallback(data.data);
            } else {
                failureCallback(data.message);
            }
        })
        .catch(error => {
            console.error('Calendar error:', error);
            failureCallback('Failed to load events');
        });
    }

    // Load appraiser calendar events
    loadAppraiserEvents(start, end, successCallback, failureCallback) {
        // Check if AJAX object is available
        if (typeof window.appraisal_ajax === 'undefined') {
            console.error('Calendar module: appraisal_ajax object not available');
            failureCallback('AJAX configuration not available');
            return;
        }
        
        
        fetch(window.appraisal_ajax.ajax_url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                action: 'get_appraiser_calendar_events',
                start: start.toISOString(),
                end: end.toISOString(),
                nonce: window.appraisal_ajax.nonce
            })
        })
        .then(response => {
            return response.json();
        })
        .then(data => {
            if (data.success) {
                successCallback(data.data);
            } else {
                failureCallback(data.message);
            }
        })
        .catch(error => {
            console.error('Calendar module: Appraiser calendar error:', error);
            failureCallback('Failed to load events');
        });
    }

    // Handle appraiser event click
    handleAppraiserEventClick(info) {
        const event = info.event;
        const extendedProps = event.extendedProps;
        
        if (extendedProps.type === 'appointment') {
            this.showAppointmentModal(event);
        } else if (extendedProps.type === 'availability') {
            // Check if this availability slot is booked
            if (extendedProps.appointment_status && extendedProps.appointment_status !== 'available') {
                // Slot is booked, show booked slot info with member name
                this.showBookedSlotInfo(event);
            } else {
                // Slot is available, show availability modal
                this.showAvailabilityModal(event);
            }
        }
    }

    // Show appointment modal
    showAppointmentModal(event) {
        const appointmentId = event.extendedProps.appointment_id;
        const status = event.extendedProps.status;
        const memberName = event.extendedProps.member_name;
        const appraiserName = event.extendedProps.appraiser_name;
        
        // Determine which name to show based on current user role
        const currentUserRole = this.getCurrentUserRole();
        let otherPartyName = '';
        let otherPartyLabel = '';
        
        if (currentUserRole === 'member') {
            // If current user is a member, show appraiser name
            otherPartyName = appraiserName || 'Unknown Appraiser';
            otherPartyLabel = 'Appraiser';
        } else {
            // If current user is an appraiser, show appraisee name
            otherPartyName = memberName || 'Unknown Appraisee';
            otherPartyLabel = 'Appraisee';
        }
        
        const modalHtml = `
            <div class="modal fade" id="appointmentModal" tabindex="-1">
                <div class="modal-dialog">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">Appointment Details</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                        </div>
                        <div class="modal-body">
                            <p><strong>${otherPartyLabel}:</strong> ${otherPartyName}</p>
                            <p><strong>Status:</strong> <span class="status-badge ${this.getStatusBadgeClass(status)}">${status.charAt(0).toUpperCase() + status.slice(1)}</span></p>
                            <p><strong>Date:</strong> ${event.start.toLocaleDateString()}</p>
                            <p><strong>Time:</strong> ${event.start.toLocaleTimeString()} - ${event.end.toLocaleTimeString()}</p>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                            <a href="/appointment/${appointmentId}/" class="btn btn-primary">View Details</a>
                        </div>
                    </div>
                </div>
            </div>
        `;
        
        this.showModal('appointmentModal', modalHtml);
    }

    // Show availability modal
    showAvailabilityModal(event) {
        const availabilityId = event.extendedProps.availability_id;
        
        const modalHtml = `
            <div class="modal fade" id="availabilityModal" tabindex="-1">
                <div class="modal-dialog">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">Availability Slot</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                        </div>
                        <div class="modal-body">
                            <p><strong>Date:</strong> ${event.start.toLocaleDateString()}</p>
                            <p><strong>Time:</strong> ${event.start.toLocaleTimeString()} - ${event.end.toLocaleTimeString()}</p>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                            <button type="button" class="btn btn-danger" onclick="appraisalTheme.deleteAvailability(${availabilityId})">Delete Slot</button>
                        </div>
                    </div>
                </div>
            </div>
        `;
        
        this.showModal('availabilityModal', modalHtml);
    }

    // Show modal helper
    showModal(modalId, modalHtml) {
        // Remove existing modal if any
        const existingModal = document.getElementById(modalId);
        if (existingModal) {
            // Hide and remove existing modal
            const existingModalInstance = bootstrap.Modal.getInstance(existingModal);
            if (existingModalInstance) {
                existingModalInstance.hide();
            }
            existingModal.remove();
        }
        
        // Add modal to body and show
        document.body.insertAdjacentHTML('beforeend', modalHtml);
        const modal = new Modal(document.getElementById(modalId));
        modal.show();
        
        // Clean up modal after it's hidden
        document.getElementById(modalId).addEventListener('hidden.bs.modal', function() {
            // Add a small delay before removing to ensure all operations complete
            setTimeout(() => {
                if (this && this.parentNode) {
                    this.remove();
                }
            }, 100);
        });
    }

    // Select time slot for booking
    selectTimeSlot(event) {
        const startDate = new Date(event.start);
        const endDate = new Date(event.end);
        
        const timeStr = startDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) + 
                       ' - ' + endDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
        const dateStr = startDate.toLocaleDateString();
        const appraiserName = event.extendedProps.appraiser_name || 'Unknown Appraiser';
        const appraiserId = event.extendedProps.appraiser_id;
        const availabilityId = event.extendedProps.availability_id;
        
        // Set the selected date, appraiser, and availability ID in hidden fields
        document.getElementById('selected-date').value = dateStr + ' at ' + timeStr;
        document.getElementById('selected-appraiser').value = appraiserName;
        
        // Add availability_id hidden field if it doesn't exist
        let availabilityIdField = document.getElementById('availability-id');
        if (!availabilityIdField) {
            availabilityIdField = document.createElement('input');
            availabilityIdField.type = 'hidden';
            availabilityIdField.id = 'availability-id';
            availabilityIdField.name = 'availability_id';
            document.getElementById('booking-form').appendChild(availabilityIdField);
        }
        availabilityIdField.value = availabilityId;
        
        // Add appraiser_id hidden field if it doesn't exist
        let appraiserIdField = document.getElementById('appraiser-id');
        if (!appraiserIdField) {
            appraiserIdField = document.createElement('input');
            appraiserIdField.type = 'hidden';
            appraiserIdField.id = 'appraiser-id';
            appraiserIdField.name = 'appraiser_id';
            document.getElementById('booking-form').appendChild(appraiserIdField);
        }
        appraiserIdField.value = appraiserId;
        
        // Update the display elements
        const datetimeDisplay = document.getElementById('selected-datetime-display');
        const appraiserDisplay = document.getElementById('selected-appraiser-display');
        
        if (datetimeDisplay) {
            datetimeDisplay.textContent = dateStr + ' at ' + timeStr;
        }
        if (appraiserDisplay) {
            appraiserDisplay.textContent = appraiserName;
        }
        
        // Show the booking form
        const bookingFormContainer = document.getElementById('booking-form-container');
        if (bookingFormContainer) {
            bookingFormContainer.style.display = 'block';
            
            // Auto-scroll to the booking form with smooth animation
            setTimeout(() => {
                bookingFormContainer.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'nearest'
                });
            }, 100); // Small delay to ensure the form is visible before scrolling
        }
        
        // Enable the book appointment button
        const bookButton = document.getElementById('book-appointment-btn');
        if (bookButton) {
            bookButton.disabled = false;
        }
    }

    // Show booked slot info
    showBookedSlotInfo(event) {
        const startDate = new Date(event.start);
        const endDate = new Date(event.end);
        
        const timeStr = startDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) + 
                       ' - ' + endDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
        const dateStr = startDate.toLocaleDateString();
        
        // Determine which name to show based on current user role
        const currentUserRole = this.getCurrentUserRole();
        let otherPartyName = '';
        let otherPartyLabel = '';
        
        if (currentUserRole === 'member') {
            // If current user is a member, show appraiser name
            otherPartyName = event.extendedProps.appraiser_name || 'Unknown Appraiser';
            otherPartyLabel = 'Appraiser';
        } else {
            // If current user is an appraiser, show appraisee name
            otherPartyName = event.extendedProps.member_name || 'Unknown Appraisee';
            otherPartyLabel = 'Appraisee';
        }
        
        // Check if we have an appointment URL to link to
        const appointmentUrl = event.extendedProps.appointment_url;
        const appointmentLink = appointmentUrl ? `<a href="${appointmentUrl}" class="btn btn-primary btn-sm" target="_blank">
            <span class="fas fa-external-link-alt me-1"></span>View Appointment Details
        </a>` : '';
        
        const modalHtml = `
            <div class="modal fade" id="bookedSlotModal" tabindex="-1">
                <div class="modal-dialog">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">Time Slot Booked</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                        </div>
                        <div class="modal-body">
                            <p>This time slot is already booked:</p>
                            <p><strong>Date:</strong> ${dateStr}</p>
                            <p><strong>Time:</strong> ${timeStr}</p>
                            <p><strong>${otherPartyLabel}:</strong> ${otherPartyName}</p>
                            ${appointmentLink ? '<div class="mt-3">' + appointmentLink + '</div>' : ''}
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                        </div>
                    </div>
                </div>
            </div>
        `;
        
        this.showModal('bookedSlotModal', modalHtml);
    }

    // Get current user role
    getCurrentUserRole() {
        // Check if user role is available in global object
        if (window.appraisal_ajax && window.appraisal_ajax.user_role) {
            return window.appraisal_ajax.user_role;
        }
        
        // Check for role indicators in the DOM
        const body = document.body;
        if (body.classList.contains('member-dashboard') || body.classList.contains('member-page')) {
            return 'member';
        }
        if (body.classList.contains('appraiser-dashboard') || body.classList.contains('appraiser-page')) {
            return 'appraiser';
        }
        
        // Check for specific page indicators
        if (window.location.pathname.includes('/member-')) {
            return 'member';
        }
        if (window.location.pathname.includes('/appraiser-')) {
            return 'appraiser';
        }
        
        // Default fallback - try to determine from calendar type
        const memberCalendar = document.getElementById('appointment-calendar') || document.getElementById('member-calendar');
        const appraiserCalendar = document.getElementById('appraiser-calendar');
        
        if (memberCalendar) {
            return 'member';
        }
        if (appraiserCalendar) {
            return 'appraiser';
        }
        
        // Default to member if we can't determine
        return 'member';
    }

    // Get status badge class
    getStatusBadgeClass(status) {
        const statusClasses = {
            'pending': 'status-pending',
            'approved': 'status-approved'
        };
        return statusClasses[status] || 'status-pending';
    }

    // Set up appraiser selection handler (no longer needed - removed appraiser selector)
    setupAppraiserSelection() {
        // Appraiser selection has been removed - calendar now shows all appraisers' availability
        // This method is kept for compatibility but does nothing
    }

    // Get calendar instance
    getCalendar(type) {
        return this.calendars.get(type);
    }

    // Refresh calendar events
    refreshCalendar(type) {
        const calendar = this.calendars.get(type);
        if (calendar) {
            calendar.refetchEvents();
        }
    }
}

// Export singleton instance
export const calendarManager = new CalendarManager();
